/** @jsxImportSource solid-js */
import { Show } from 'solid-js'
import { cn } from '../cn'

// Keyframes ship with the component (namespaced with a forest- prefix)
// because consuming apps' tailwind configs don't define them — a
// Tailwind-supplied animation would silently not run in any app that forgot
// to add the keyframes. Head-injection preserves what ADR 0007's
// "no shipped CSS" rule protects: no import step to forget, no packed CSS
// asset.
const KEYFRAMES = `
@keyframes forest-spin-fade {
  0% { opacity: 1; }
  100% { opacity: 0.15; }
}
@keyframes forest-spinner-rotate {
  100% { transform: rotate(360deg); }
}
@keyframes forest-spinner-dash {
  0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
  100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
}
`

const SPINNER_BARS = 12

// Injected into <head> rather than rendered inline: an inline <style> inside
// a host's button would leak the keyframes text into its accessible name.
let keyframesInjected = false
function ensureKeyframes() {
  if (keyframesInjected) return
  keyframesInjected = true
  const style = document.createElement('style')
  style.textContent = KEYFRAMES
  document.head.append(style)
}

export type SpinnerVariant = 'ios' | 'material' | 'platform'

// Which spinner to draw. `material` (the default) is the arc everywhere;
// `ios` is the 12-bar wheel everywhere; `platform` follows the launch URL's
// `?platform=` — the wheel on `ios`, the arc for anything else, including a
// missing or unrecognised value — for the surfaces that should feel native.
export function Spinner(props: { class?: string; variant?: SpinnerVariant }) {
  ensureKeyframes()
  return (
    <Show
      when={resolveVariant(props.variant ?? 'material') === 'ios'}
      fallback={<MaterialSpinner class={props.class} />}
    >
      <IosSpinner class={props.class} />
    </Show>
  )
}

function resolveVariant(variant: SpinnerVariant): 'ios' | 'material' {
  if (variant !== 'platform') return variant
  const platform = new URLSearchParams(window.location.search).get('platform')
  return platform === 'ios' ? 'ios' : 'material'
}

function IosSpinner(props: { class?: string }) {
  return (
    <div
      role="status"
      class={cn('relative size-10 text-[#4AB09A]', props.class)}
    >
      {Array.from({ length: SPINNER_BARS }, (_, i) => (
        <span
          class="absolute top-1/2 left-1/2 rounded-full bg-current"
          style={{
            width: '11%',
            height: '26%',
            'margin-left': '-5.5%',
            'margin-top': '-13%',
            // Every bar pivots on the box centre, so the ring they form is
            // centred in the box. Pivoting on `center bottom` — the bar's
            // own bottom edge, 13% of the box below its centre — put the
            // whole ring that far low and hung it over the box's bottom
            // edge. -130% keeps the ring the size it has always been (inner
            // radius 20.8% of the box, outer 46.8%): only its centre moves.
            'transform-origin': 'center',
            transform: `rotate(${i * 30}deg) translateY(-130%)`,
            animation: 'forest-spin-fade 1s linear infinite',
            'animation-delay': `${-(1 - i / SPINNER_BARS)}s`,
          }}
        />
      ))}
    </div>
  )
}

function MaterialSpinner(props: { class?: string }) {
  return (
    <svg
      role="status"
      class={cn('size-10 text-[#4AB09A]', props.class)}
      viewBox="0 0 50 50"
      style={{ animation: 'forest-spinner-rotate 1.4s linear infinite' }}
    >
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="currentColor"
        stroke-width="4"
        stroke-linecap="round"
        stroke-dasharray="1,150"
        stroke-dashoffset="0"
        style={{ animation: 'forest-spinner-dash 1.4s ease-in-out infinite' }}
      />
    </svg>
  )
}
